import React from 'react';
import Modal from '../components/Modal';
import type { PermissionLevel, PermissionResource } from './permission-catalog';
import {
  permissionDeniedMessage,
  permissionRequirementLabel,
  type PermissionPhraseOverrides,
} from './permission-messages';
import './permission-denied.css';

export interface PermissionDeniedModalProps extends PermissionPhraseOverrides {
  isOpen: boolean;
  onClose: () => void;
  resource: PermissionResource;
  level: PermissionLevel;
  testId?: string;
}

/**
 * What a guarded action opens INSTEAD of doing the thing.
 *
 * The old pattern was `canEdit && <Button/>` — the control simply was not
 * there. Now the control is always there and the click lands here: "You do not
 * have permission to add cards", in place of the Add Card modal.
 *
 * The click never reaches the real handler, so no request is made. That is a
 * UX decision, not the protection: `requirePermission` on the route would
 * refuse the write regardless, and this modal exists so the refusal is
 * explained up front instead of arriving as a 403 from a form the user already
 * filled in.
 */
const PermissionDeniedModal: React.FC<PermissionDeniedModalProps> = ({
  isOpen,
  onClose,
  resource,
  level,
  verb,
  subject,
  testId = 'permission-denied-modal',
}) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Permission needed">
    <div
      data-testid={testId}
      data-permission-resource={resource}
      data-permission-level={level}>
      <p className="permission-denied-modal-message">
        {permissionDeniedMessage({ resource, level, verb, subject })}
      </p>
      <p className="descriptionText permission-denied-modal-hint">
        Ask a team administrator for{' '}
        <span className="permission-denied-notice-requirement">
          {permissionRequirementLabel(resource, level)}
        </span>{' '}
        if you need to do this.
      </p>
    </div>
    <div className="delete-modal-actions">
      <button
        type="button"
        className="lightButton"
        data-testid={`${testId}-dismiss`}
        onClick={onClose}>
        Close
      </button>
    </div>
  </Modal>
);

export default PermissionDeniedModal;
